import { useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { APP_TITLE } from "@/const";
import { BrandLogo } from "@/components/BrandLogo";
import { useTranslation } from "@/i18n";

export default function Register() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const utils = trpc.useUtils();

  const registerMutation = trpc.auth.register.useMutation({
    onSuccess: async (data) => {
      if (data.requiresActivation) {
        // Account created but needs admin activation
        toast.success(t.register.accountCreatedPendingActivation);
        setLocation("/login");
      } else {
        // Account created and user is logged in
        toast.success(t.register.accountCreated);
        // Invalidate auth cache to trigger refetch
        await utils.auth.me.invalidate();
        setLocation("/");
      }
    },
    onError: (error) => {
      toast.error(error.message || t.register.registrationFailed);
      setIsLoading(false);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t.register.passwordsDontMatch);
      return;
    }

    if (password.length < 8) {
      toast.error(t.register.passwordTooShort);
      return;
    }

    setIsLoading(true);
    registerMutation.mutate({ name, email, password });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <BrandLogo size={64} rounded showName />
          </div>
          <CardTitle className="text-2xl font-bold">{t.register.title}</CardTitle>
          <CardDescription>
            {t.register.subtitle.replace('{appTitle}', APP_TITLE)}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">{t.register.name}</Label>
              <Input
                id="name"
                type="text"
                placeholder={t.register.namePlaceholder}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t.register.email}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t.register.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t.register.password}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t.register.passwordPlaceholder}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                minLength={8}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t.register.confirmPassword}</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder={t.register.confirmPasswordPlaceholder}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
                minLength={8}
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4 pt-6">
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? t.common.loading : t.register.registerButton}
            </Button>
            <div className="text-sm text-center text-muted-foreground">
              {t.register.alreadyHaveAccount}{" "}
              <a
                href="/login"
                className="text-primary hover:underline font-medium"
              >
                {t.register.loginLink}
              </a>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
